import { request } from "$/lib/api"
import {
  Route,
  SimulationSettings,
  SimulationSettingsResponse,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

async function fetchSimulationSettings(
  storeId: string,
): Promise<SimulationSettingsResponse> {
  return await request<SimulationSettingsResponse>({
    url: populateRoute(Route.STATS__SIMULATION_SETTINGS, storeId),
    method: "GET",
  })
}

async function updateSimulationSettings(
  storeId: string,
  data: SimulationSettings,
) {
  return await request<void>({
    url: populateRoute(Route.STATS__UPDATE_SIMULATION_SETTINGS, storeId),
    method: "PUT",
    data,
  })
}

async function computeSimulationProfitShares(storeId: string) {
  return await request<void>({
    url: populateRoute(Route.STATS__COMPUTE_SIMULATION_PROFIT_SHARES, storeId),
    method: "POST",
  })
}

export function useSimulationSettingsMutation(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateSimulationQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries(simulationSettingsQueries.detail(storeId)),
      // Both calculators start from the settings.
      queryClient.invalidateQueries({
        queryKey: ["stats", "inspection-simulation", storeId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["stats", "price-simulation", storeId],
      }),
    ])
  }

  const updateMutation = useMutation({
    mutationFn: (data: SimulationSettings) =>
      updateSimulationSettings(storeId, data),
    onSettled: invalidateSimulationQueries,
  })

  const computeProfitSharesMutation = useMutation({
    mutationFn: () => computeSimulationProfitShares(storeId),
    onSettled: invalidateSimulationQueries,
  })

  return { updateMutation, computeProfitSharesMutation }
}

const simulationSettingsQueries = {
  detail: (storeId: string) =>
    queryOptions({
      queryKey: ["simulation-settings", storeId],
      queryFn: () => fetchSimulationSettings(storeId),
    }),
}

export default simulationSettingsQueries
